import gql from 'graphql-tag';

export default class TimelineFragments {
  static participantData = gql`
    fragment participantData on ParticipantData {
      role
      firstName
      lastName
      title
      companyName
      email
    }
  `;

  static createdParticipantData = gql`
    fragment createdParticipantData on ParticipantCreatedData {
      role
      firstName
      lastName
      title
      companyName
      email
    }
  `;

  static visitedEventLogData = gql`
    fragment visitedEventLogData on DealroomVisitedEventLogData {
      type
      participantId
      sessionId
      trackingConsent
      isFirstVisit
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static publishedEventLogData = gql`
    fragment publishedEventLogData on DealroomPublishedEventLogData {
      type
      version
      participantId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static unpublishedEventLogData = gql`
    fragment unpublishedEventLogData on DealroomUnpublishedEventLogData {
      type
      participantId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static participantCreatedEventLogData = gql`
    fragment participantCreatedEventLogData on DealroomParticipantCreatedEventLogData {
      type
      participantId
      createdParticipantId
      creationSource
      createdParticipantData {
        ...createdParticipantData
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.createdParticipantData}
    ${TimelineFragments.participantData}
  `;

  static systemParticipantCreatedEventLogData = gql`
    fragment systemParticipantCreatedEventLogData on DealroomSystemParticipantCreatedEventLogData {
      type
      createdParticipantId
      creationSource
      createdParticipantData {
        ...createdParticipantData
      }
    }
    ${TimelineFragments.createdParticipantData}
  `;

  static postCreatedEventLogData = gql`
    fragment postCreatedEventLogData on DealroomPostCreatedEventLogData {
      type
      participantId
      postId
      notificationChannels
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static actionPlanUpdatedEventLogData = gql`
    fragment actionPlanUpdatedEventLogData on DealroomActionPlanUpdatedEventLogData {
      type
      participantId
      actionPlanId
      updateActionPlan: update {
        title
        description
        moveMilestone {
          id
          targetIndex
        }
        visible
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static milestoneEventLogData = gql`
    fragment milestoneEventLogData on DealroomMilestoneEventLogData {
      type
      participantId
      actionPlanId
      milestoneId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static taskEventLogData = gql`
    fragment taskEventLogData on DealroomTaskEventLogData {
      type
      participantId
      actionPlanId
      milestoneId
      taskId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static taskUpdatedEventLogData = gql`
    fragment taskUpdatedEventLogData on DealroomTaskUpdatedEventLogData {
      type
      participantId
      actionPlanId
      actionPlanTitle
      milestoneId
      milestoneIndex
      milestoneTitle
      taskId
      taskData {
        title
        description
        status
        participantIds
      }
      updateTask: update {
        title
        participantIds
        description
        deadline
        status
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static fileUploadedEventLogData = gql`
    fragment fileUploadedEventLogData on DealroomFileUploadedEventLogData {
      type
      fileId
      size
      participantId
      fileData {
        name
        extension
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static fileDownloadedEventLogData = gql`
    fragment fileDownloadedEventLogData on DealroomFileDownloadedEventLogData {
      type
      fileId
      participantId
      fileData {
        name
        extension
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static fileDeletedEventLogData = gql`
    fragment fileDeletedEventLogData on DealroomFileDeletedEventLogData {
      type
      fileId
      participantId
      fileData {
        name
        extension
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static contextualCommentReplyCreatedEventLogData = gql`
    fragment contextualCommentReplyCreatedEventLogData on DealroomContextualCommentReplyCreatedEventLogData {
      type
      commentId
      replyId
      participantId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static contextualCommentUpdatedEventLogData = gql`
    fragment contextualCommentUpdatedEventLogData on DealroomContextualCommentUpdatedEventLogData {
      type
      commentId
      participantId
      status
      updatedData {
        status
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static deletedEventLogData = gql`
    fragment deletedEventLogData on DealroomDeletedEventLogData {
      type
      participantId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static featureSettingsUpdatedEventLogData = gql`
    fragment featureSettingsUpdatedEventLogData on DealroomFeatureSettingsUpdatedEventLogData {
      type
      updateFeatureSettings: update {
        disableActionPlan
        disableChat
        disableComments
        disableFiles
      }
      participantId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static accessRuleUpdatedEventLogData = gql`
    fragment accessRuleUpdatedEventLogData on DealroomAccessRuleUpdatedEventLogData {
      type
      participantId
      update
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static conversationMessageEventLogData = gql`
    fragment conversationMessageEventLogData on DealroomConversationMessageEventLogData {
      type
      participantId
      conversationId
      messageId
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static participantDeletedEventLogData = gql`
    fragment participantDeletedEventLogData on DealroomParticipantDeletedEventLogData {
      type
      participantId
      deletedParticipant {
        participantId
        role
        firstName
        lastName
        title
        companyName
        email
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.participantData}
  `;

  static entityAdminRoomOwnerUpdatedEventLogData = gql`
    fragment entityAdminRoomOwnerUpdatedEventLogData on DealroomEntityAdminRoomOwnerUpdatedEventLogData {
      type
      userId
      oldRoomOwnerUserId
      newRoomOwnerUserId
    }
  `;

  static entityAdminParticipantCreatedEventLogData = gql`
    fragment entityAdminParticipantCreatedEventLogData on DealroomEntityAdminParticipantCreatedEventLogData {
      type
      userId
      createdParticipantId
      creationSource
      createdParticipantData {
        ...createdParticipantData
      }
      participantData {
        ...participantData
      }
    }
    ${TimelineFragments.createdParticipantData}
    ${TimelineFragments.participantData}
  `;

  static participantRoomOwnerUpdatedEventLogData = gql`
    fragment participantRoomOwnerUpdatedEventLogData on DealroomParticipantRoomOwnerUpdatedEventLogData {
      type
      participantId
      oldRoomOwnerUserId
      newRoomOwnerUserId
    }
    ${TimelineFragments.participantData}
  `;

  static entityAdminPublishedEventLogData = gql`
    fragment entityAdminPublishedEventLogData on DealroomEntityAdminPublishedEventLogData {
      type
      userId
    }
  `;

  static entityAdminUnpublishedEventLogData = gql`
    fragment entityAdminUnpublishedEventLogData on DealroomEntityAdminUnpublishedEventLogData {
      type
      userId
    }
  `;
}
